import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Calendar, MapPin, Car, Sparkles, CheckCircle } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: Calendar,
      title: 'Book',
      description: 'Schedule via phone, WhatsApp, or our online platform. Choose your preferred time slot.',
    },
    {
      number: '02',
      icon: CheckCircle,
      title: 'Confirm',
      description: 'Select your preferred service, date, and time. We\'ll confirm your booking.',
    },
    {
      number: '03',
      icon: MapPin,
      title: 'We Visit',
      description: 'Our trained PRO team arrives at your location with all tools and premium materials.',
    },
    {
      number: '04',
      icon: Sparkles,
      title: 'Premium Care',
      description: 'Your car is meticulously cleaned, detailed, and polished to perfection.',
    },
    {
      number: '05',
      icon: Car,
      title: 'Enjoy',
      description: 'Drive a refreshed vehicle – without leaving your home or office.',
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="how-it-works-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            Simple Process
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            How It Works
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            Our simple 5-step premium care process ensures you get the best service without any hassle.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-24 left-[10%] right-[10%] h-1 bg-gradient-to-r from-[#1E88E5] via-[#F7931E] to-[#1E88E5] rounded-full"></div>
            
            <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className="relative bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1 text-center"
                  data-testid={`step-${index}`}
                >
                  <div className="relative z-10">
                    <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D] rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                      <step.icon className="h-8 w-8 text-white" />
                    </div>
                    <span className="inline-block bg-[#F7931E] text-white text-sm font-bold px-3 py-1 rounded-full mb-4">
                      Step {step.number}
                    </span>
                    <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-[#64748B] text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visual Flow */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Book → We Arrive → We Care → You Relax
            </h2>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              It's that simple. Premium car care without stepping out of your comfort zone.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'Book', desc: 'Call, WhatsApp or online', color: 'bg-[#1E88E5]' },
              { title: 'We Arrive', desc: 'At your doorstep', color: 'bg-[#0B2C5D]' },
              { title: 'We Care', desc: 'Premium service', color: 'bg-[#F7931E]' },
              { title: 'You Relax', desc: 'Enjoy your clean car', color: 'bg-[#1E88E5]' },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className={`${item.color} text-white p-8 rounded-2xl text-center`}>
                  <h3 className="font-heading text-2xl font-bold mb-2">{item.title}</h3>
                  <p className="text-white/80">{item.desc}</p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="h-6 w-6 text-[#64748B]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Options */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0B2C5D] mb-4">
              Multiple Ways to Book
            </h2>
            <p className="text-[#64748B]">Choose the booking method that's most convenient for you.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <a
              href="tel:9044499957"
              className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 text-center group hover:-translate-y-1"
              data-testid="book-phone"
            >
              <div className="w-16 h-16 mx-auto bg-[#1E88E5]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#1E88E5] transition-colors">
                <Phone className="h-8 w-8 text-[#1E88E5] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-2">Call Us</h3>
              <p className="text-[#F7931E] font-semibold">9044499957</p>
            </a>

            <a
              href="https://wa.me/919044499957?text=Hi%20Washora,%20I'd%20like%20to%20book%20a%20car%20wash%20service."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 text-center group hover:-translate-y-1"
              data-testid="book-whatsapp"
            >
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-green-500 transition-colors">
                <svg className="w-8 h-8 text-green-600 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-2">WhatsApp</h3>
              <p className="text-green-600 font-semibold">Message Us</p>
            </a>

            <Link
              to="/booking"
              className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 text-center group hover:-translate-y-1"
              data-testid="book-online"
            >
              <div className="w-16 h-16 mx-auto bg-[#F7931E]/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-[#F7931E] transition-colors">
                <Calendar className="h-8 w-8 text-[#F7931E] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-heading text-xl font-bold text-[#0B2C5D] mb-2">Online</h3>
              <p className="text-[#F7931E] font-semibold">Book Now</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Experience Premium Care?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Book your first doorstep car wash today and see why customers love Washora.
          </p>
          <Link
            to="/booking"
            className="inline-flex items-center bg-[#F7931E] text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#E08215] transition-all transform hover:-translate-y-0.5 shadow-lg"
            data-testid="hiw-book-now"
          >
            Book Now <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
